<template>
  <section>
    <v-card class="pa-4 mb-4">
      <!-- header filters -->
      <v-row>
        <v-col>
          <h5 class="grey--text">
            {{ t.filterByAnalysisStatus }}
          </h5>
        </v-col>
      </v-row>

      <v-row style="flex-wrap: nowrap;">
        <v-col class="flex-grow-1">
          <v-checkbox
            v-for="option of analysisOptions"
            :key="option.key"
            v-model="option.val"
            :label="`${option.name} (${option.occurences || 0})`"
            hide-details
            class="mr-5 d-inline-block"
            @click="filter"
          />
        </v-col>
        <v-col class="flex-grow-1">
          <v-text-field
            v-model="searchTerm"
            :label="t.filterByTitleOfSummary"
            outlined
            class="mr-5"
            @keyup.prevent="filter"
            @keyup.esc.prevent="searchTerm = ''"
          />
        </v-col>
        <v-col class="flex-grow-0">
          <v-btn
            color="primary"
            class="mt-3"
            @click="resetFilters"
          >
            {{ t.resetFilters }}
          </v-btn>
        </v-col>
      </v-row>

      <v-spacer class="py-5" />

      <v-row>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            text
            @click="onExpandToggle"
          >
            {{ expandButtonText }}
            <v-icon
              right
              dark
            >
              unfold_more
            </v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- columns names and expand / collapse -->
    <v-row class="mt-8">
      <v-col
        cols="2"
        class="pb-0"
      >
        <h5 class="pl-5">
          {{ t.statutoryCitation }}
        </h5>
      </v-col>
      <v-col
        cols="10"
        class="d-flex justify-space-between pb-0"
      >
        <h5 class="pl-2">
          {{ t.shortTitle }}
        </h5>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-expansion-panels
          v-model="expandedPanels"
          multiple
          rounded
        >
          <v-expansion-panel
            v-for="(section, index) of eligibilitySections"
            :key="index"
          >
            <template v-if="!section.invisible">
              <!-- accordion header -->
              <v-expansion-panel-header>
                <v-row no-gutters>
                  <v-col cols="2">
                    <h5 class="d-inline-block">
                      {{ section.statutoryCitation }}
                    </h5>
                    <a
                      :href="section.link"
                      target="_new"
                    >
                      <v-icon small>mdi-arrow-top-right</v-icon>
                    </a>
                  </v-col>
                  <v-col>
                    <v-row>
                      <v-col class="d-flex justify-space-between">
                        <h4>{{ section.shortTitle }}</h4>

                        <h5
                          class="rounded-lg py-2 px-4 mr-4 white--text"
                          :class="[getSectionStatus(section).color]"
                        >
                          {{ getSectionStatus(section).name }}
                        </h5>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col>
                        <p class="text-body-2">
                          {{ section.summary }}
                        </p>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>

              <!-- accordion content -->
              <v-expansion-panel-content class="">
                <v-row>
                  <v-col cols="8">
                    <v-textarea
                      v-model="section.narrative"
                      auto-grow
                      outlined
                      :label="t.narrative"
                      :disabled="section.locked || !peUserCanUpdate"
                      @change="updateSectionNarrative(section)"
                    />
                  </v-col>
                  <v-col>
                    <v-select
                      v-model="section.status"
                      outlined
                      :items="analysisOptions"
                      item-text="name"
                      item-value="key"
                      :label="t.analysisStatus"
                      :disabled="!peUserCanUpdate"
                      @change="updateSectionStatus(section)"
                    />

                    <template v-if="section.narrative && peUserCanUpdate">
                      <template v-if="section.locked">
                        <v-btn
                          depressed
                          color="primary"
                          class="float-right"
                          @click="switchEditing(section)"
                        >
                          {{ t.edit }}
                          <v-icon
                            small
                            class="ml-2"
                          >
                            mdi-pencil
                          </v-icon>
                        </v-btn>
                      </template>

                      <template v-else>
                        <v-btn
                          depressed
                          color="primary"
                          class="float-right"
                          @click="switchEditing(section)"
                        >
                          {{ t.lock }}
                          <v-icon
                            small
                            class="ml-2"
                          >
                            mdi-lock
                          </v-icon>
                        </v-btn>
                      </template>
                    </template>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </template>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { ELIGIBILITY_ASSISTANT_NAMESPACE } from '@/store/eligibility-assistant'
import { ELIGIBILITY_SECTIONS } from '@/data/Party/eligibilityAssistant'
import { PARTY_NAMESPACE } from '@/store/party'

export default {
  name: 'PEElligibilityAssistant',

  props: {
    id: {
      type: String || Number,
      required: true,
    },
  },

  data () {
    return {
      expandedPanels: [],
      partyEligibilities: [],
      searchTerm: '',
      t: {
        analysisStatus: 'Analysis Status',
        collapseAll: 'Collapse All',
        edit: 'Edit',
        expandAll: 'Expand All',
        filterByAnalysisStatus: 'Filter by Analysis Status',
        filterByTitleOfSummary: 'Filter by Title or Summary',
        lock: 'Lock',
        narrative: 'Narrative',
        resetFilters: 'Reset',
        shortTitle: 'Short Title',
        statutoryCitation: 'Statutory Citation',
      },
    }
  },

  computed: {
    analysisOptionsFilter () {
      return this.analysisOptions.filter(option => option.val)
    },

    expandButtonText () {
      return this.shouldExpandPanels ? this.t.expandAll : this.t.collapseAll
    },

    partyId () { return this.id },

    shouldExpandPanels () {
      return this.expandedPanels.length < 2
    },

    ...mapGetters(ELIGIBILITY_ASSISTANT_NAMESPACE, [
      'analysisOptions',
      'eligibilitySections',
    ]),

    ...mapGetters(PARTY_NAMESPACE, [
      'peUserCanUpdate',
    ]),
  },

  async created () {
    this.peGetPartySection({ id: this.id }).
      catch(() => this.displayAlert('error', `No Party found with the id: ${this.id}`))

    const partyEligibilities = await this.getPartyEligibilities(this.partyId)
    if (!partyEligibilities) return

    this.setEligibilitySections(ELIGIBILITY_SECTIONS.map(section => {
      return { ...section, ...partyEligibilities.find(s => s.identifier === section.identifier) }
    }))

    this.updateAnalysisOptions()
  },

  beforeDestroy () {
    // Reset the data to prevent caching
    this.setEligibilitySections(ELIGIBILITY_SECTIONS)
  },

  methods: {
    filter () {
      this.setEligibilitySections(
        this.eligibilitySections.
          map(option => {
            let invisible

            if (this.searchTerm) {
              const shortTitle = option.shortTitle.toLowerCase()
              const summary = option.summary.toLowerCase()
              const searchTerm = this.searchTerm.toLowerCase()
              invisible = shortTitle.indexOf(searchTerm) === -1 && summary.indexOf(searchTerm) === -1
            }

            if (!invisible) {
              const filter = this.analysisOptionsFilter
              invisible = filter.length ? !filter.map(option => option.key).includes(option.status) : false
            }

            return { ...option, invisible }
          })
      )

      this.updateAnalysisOptions()
    },

    getSectionStatus (section) {
      const status = section.status || 'no-analysis'
      return this.analysisOptions.find(option => option.key === status)
    },

    onExpandToggle () {
      if (this.shouldExpandPanels) this.expandedPanels = this.eligibilitySections.map((k, i) => i)
      else this.expandedPanels = []
    },

    resetFilters () {
      this.searchTerm = ''
      this.setEligibilitySections(this.eligibilitySections.map(section => ({ ...section, invisible: false })))
      this.setAnalysisOptions(
        this.analysisOptions.map(option => {
          const filter = this.eligibilitySections.filter(section => {
            return section.status === option.key && section.invisible !== true
          })

          return { ...option, occurences: filter.length, val: false }
        })
      )
    },

    switchEditing (section) {
      const eligibilitySection = this.eligibilitySections.find(s => s.identifier === section.identifier)
      eligibilitySection.locked = !eligibilitySection.locked

      this.savePartyEligibility({ partyId: this.partyId, section: section })
    },

    updateAnalysisOptions () {
      this.setAnalysisOptions(
        this.analysisOptions.map(option => {
          const filter = this.eligibilitySections.filter(s => s.status === option.key && s.invisible !== true)

          return { ...option, occurences: filter.length }
        })
      )
    },

    updateSectionNarrative (section) {
      this.eligibilitySections.find(s => s.identifier === section.identifier).narrative = section.narrative

      this.savePartyEligibility({ partyId: this.partyId, section: section })
    },

    updateSectionStatus (section) {
      this.eligibilitySections.find(s => s.identifier === section.identifier).status = section.status

      this.filter()

      this.savePartyEligibility({ partyId: this.partyId, section: section })
    },

    ...mapActions(PARTY_NAMESPACE, [
      'peGetPartySection',
    ]),

    ...mapMutations(ELIGIBILITY_ASSISTANT_NAMESPACE, [
      'setAnalysisOptions',
      'setEligibilitySections',
    ]),

    ...mapActions(ELIGIBILITY_ASSISTANT_NAMESPACE, [
      'getPartyEligibilities',
      'savePartyEligibility',
    ]),
  },
}
</script>
